import { useEffect, useRef } from 'react';

/**
 * Hook for functional components to get the previous value of a prop or state
 */
export default function usePrevious<Type>(value: Type, initialValue: Type): Type;
export default function usePrevious<Type>(value: Type, initialValue?: Type): Type | undefined;
export default function usePrevious<Type>(value: Type, initialValue?: Type): Type | undefined {
    const ref = useRef<Type | undefined>(initialValue);

    useEffect(() => {
        ref.current = value;
    }, [value]);

    return ref.current;
}

/**
 * You can use this hook as a drop-in replacement for useEffect. It will print debugging
 * information to the console when any of the dependencies change, so you can understand
 * why a hook is re-running.
 *
 * Additionally, you can pass in a 3rd argument, which is the variable names (as strings)
 * of all the dependencies, to make the logs a little prettier.
 */
export const __useEffectDebugger = (
    effectHook: React.EffectCallback,
    dependencies: React.DependencyList,
    dependencyNames: string[] = [],
) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const previousDeps = usePrevious(dependencies, []);

    const changedDeps = dependencies.reduce<Record<string, { before: unknown; after: unknown }>>(
        (accum, dependency, index) => {
            if (dependency !== previousDeps[index]) {
                const keyName = dependencyNames[index] || index;
                return {
                    ...accum,
                    [keyName]: {
                        before: previousDeps[index],
                        after: dependency,
                    },
                };
            }

            return accum;
        },
        {},
    );

    if (Object.keys(changedDeps).length) {
        // the whole point of this is console logging!
        // eslint-disable-next-line no-console
        console.log('[use-effect-debugger] ', changedDeps);
    }

    // eslint doesn't understand! The dependencies are exhaustive, but eslint can't figure that out
    // eslint-disable-next-line react-hooks/exhaustive-deps, react-hooks/rules-of-hooks
    useEffect(effectHook, dependencies);
};
